"use client";

import { useCallback, useEffect, useState } from "react";
import Script from "next/script";
import { MOT_COOKIE_PREFERENCES_CHANGED } from "@/components/CookieConsent";
import { isAnalyticsAllowed } from "@/lib/analytics-consent";
import { isProdPublicTrackingEnabled } from "@/lib/public-deploy-env";
import { onAnalyticsAllowanceChanged } from "@/lib/vendor-tracking-lifecycle";

export function PlausibleLoader({ domain }: { domain: string | undefined }) {
  const [enabled, setEnabled] = useState(false);
  const prod = isProdPublicTrackingEnabled();

  const applyAllowance = useCallback((allowed: boolean) => {
    if (!allowed) {
      onAnalyticsAllowanceChanged(false);
      setEnabled(false);
      return;
    }
    setEnabled(true);
  }, []);

  const sync = useCallback(() => {
    applyAllowance(isAnalyticsAllowed());
  }, [applyAllowance]);

  useEffect(() => {
    if (!prod) return;
    sync();
    function onPrefs() {
      applyAllowance(isAnalyticsAllowed());
    }
    window.addEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
    return () => window.removeEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
  }, [prod, sync, applyAllowance]);

  useEffect(() => {
    if (typeof window === "undefined" || !prod || !domain?.trim()) return;
    try {
      if (enabled) {
        localStorage.removeItem("plausible_ignore");
      } else {
        localStorage.setItem("plausible_ignore", "true");
      }
    } catch {
      /* storage blocked */
    }
  }, [prod, domain, enabled]);

  if (!prod || !domain?.trim() || !enabled) {
    return null;
  }

  return (
    <Script
      defer
      data-domain={domain.trim()}
      src="https://plausible.io/js/script.js"
      strategy="afterInteractive"
    />
  );
}
